import {
  AlertCircleIcon,
  CheckCircle2Icon,
  CloudIcon,
  LaptopIcon,
  LoaderIcon,
  RefreshCwIcon,
  TerminalIcon,
  UnplugIcon,
} from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";

export type CloudProviderExecutionTarget = "local" | "cloud" | "remote";
export type CloudProviderConnectionStatus = "loading" | "connected" | "disconnected" | "error";

export interface CloudProviderConnection {
  readonly status: CloudProviderConnectionStatus;
  readonly message?: string | null;
}

export interface CloudProviderConnectionPresentation {
  readonly label: string;
  readonly description: string;
  readonly tone: "muted" | "success" | "error" | "warning";
}

export interface CloudProviderSettingsProps {
  readonly providerName: string;
  readonly subscriptionTitle: string;
  readonly providerIcon: ReactNode;
  readonly providerAccentClassName?: string;
  readonly sectionId: string;
  readonly sectionTitle: string;
  readonly sectionTestId: string;
  readonly statusTestId: string;
  readonly connection: CloudProviderConnection;
  readonly presentation: CloudProviderConnectionPresentation;
  readonly environmentKind: CloudProviderExecutionTarget;
  readonly environmentLabel: string;
  readonly executionDescription: string;
  readonly subscriptionDescription: string;
  readonly cloudReady: boolean;
  readonly authAvailable?: boolean;
  readonly authUnavailableReason?: string;
  readonly loginCommand: string;
  readonly cloudWarningTitle: string;
  readonly cloudWarningDescription: string;
  readonly billingDescription: ReactNode;
  readonly readOnly?: boolean;
  readonly onConnect?: () => Promise<void>;
  readonly onDisconnect?: () => Promise<void>;
  readonly onRetry?: () => void;
}

type ActionState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "pending"; readonly message: string }
  | { readonly status: "error"; readonly message: string };

function safeErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim().length > 0 ? error.message : fallback;
}

function connectionBadgeVariant(
  tone: CloudProviderConnectionPresentation["tone"],
): "outline" | "success" | "error" {
  if (tone === "success") return "success";
  if (tone === "error") return "error";
  return "outline";
}

function cloudExecutionTargetLabel(target: CloudProviderExecutionTarget): string {
  if (target === "cloud") return "Cloud";
  if (target === "remote") return "Remote";
  return "Local";
}

function executionTargetDescription(target: CloudProviderExecutionTarget): string {
  if (target === "cloud") {
    return "This AgentCore environment is the selected provider target and may incur Fabrica runtime charges.";
  }
  if (target === "remote") {
    return "This remote device is the selected provider target; Fabrica adds no managed runtime charge.";
  }
  return "This device is the selected provider target. Local execution does not use AgentCore or add a managed runtime charge.";
}

function ConnectionStatus({
  connection,
  presentation,
  testId,
}: {
  readonly connection: CloudProviderConnection;
  readonly presentation: CloudProviderConnectionPresentation;
  readonly testId: string;
}) {
  const StatusIcon =
    connection.status === "connected"
      ? CheckCircle2Icon
      : connection.status === "error"
        ? AlertCircleIcon
        : connection.status === "loading"
          ? LoaderIcon
          : UnplugIcon;

  return (
    <div className="flex min-w-0 items-center gap-2" data-testid={testId}>
      <StatusIcon
        className={
          connection.status === "loading"
            ? "size-3.5 animate-spin text-muted-foreground"
            : connection.status === "connected"
              ? "size-3.5 text-success"
              : connection.status === "error"
                ? "size-3.5 text-destructive"
                : "size-3.5 text-muted-foreground"
        }
        aria-hidden
      />
      <Badge variant={connectionBadgeVariant(presentation.tone)} size="sm">
        {presentation.label}
      </Badge>
    </div>
  );
}

export function CloudProviderSettings({
  providerName,
  subscriptionTitle,
  providerIcon,
  providerAccentClassName = "border-border/60 bg-muted/15",
  sectionId,
  sectionTitle,
  sectionTestId,
  statusTestId,
  connection,
  presentation,
  environmentKind,
  environmentLabel,
  executionDescription,
  subscriptionDescription,
  cloudReady,
  authAvailable = false,
  authUnavailableReason = "This environment cannot manage subscription authentication.",
  loginCommand,
  cloudWarningTitle,
  cloudWarningDescription,
  billingDescription,
  readOnly = false,
  onConnect,
  onDisconnect,
  onRetry,
}: CloudProviderSettingsProps) {
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [actionState, setActionState] = useState<ActionState>({ status: "idle" });

  const actionBusy = actionState.status === "loading";
  const SectionIcon =
    environmentKind === "cloud"
      ? CloudIcon
      : environmentKind === "remote"
        ? TerminalIcon
        : LaptopIcon;
  const subscriptionDescriptionWithAvailability = authAvailable
    ? subscriptionDescription
    : `${subscriptionDescription} ${authUnavailableReason}`;

  const closeConnectDialog = (open: boolean) => {
    setConnectDialogOpen(open);
    if (!open && !actionBusy) {
      setActionState({ status: "idle" });
    }
  };

  const submitConnection = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (actionBusy) return;
    if (!onConnect) {
      setActionState({
        status: "error",
        message:
          "The environment terminal connection is not configured in this build yet. Your setup is unchanged.",
      });
      return;
    }

    setActionState({ status: "loading" });
    try {
      await onConnect();
      setConnectDialogOpen(false);
      setActionState({
        status: "pending",
        message: `Login command copied. Open any thread in ${environmentLabel}, open its terminal, paste the command, finish OAuth, then refresh status.`,
      });
    } catch (error) {
      setActionState({
        status: "error",
        message: safeErrorMessage(error, `Could not connect the ${providerName} subscription.`),
      });
    }
  };

  const disconnect = async () => {
    if (actionBusy) return;
    if (!onDisconnect) {
      setActionState({
        status: "error",
        message: `${providerName} connection management is not configured in this build yet. No connection was changed.`,
      });
      return;
    }
    setActionState({ status: "loading" });
    try {
      await onDisconnect();
      setActionState({
        status: "pending",
        message: `Logout command copied. Open any thread in ${environmentLabel}, open its terminal, paste the command, then refresh status.`,
      });
    } catch (error) {
      setActionState({
        status: "error",
        message: safeErrorMessage(error, `Could not disconnect the ${providerName} subscription.`),
      });
    }
  };

  const actionPending = actionState.status === "pending" ? actionState.message : null;
  const actionError = actionState.status === "error" ? actionState.message : null;

  return (
    <SettingsSection
      id={sectionId}
      title={sectionTitle}
      icon={<SectionIcon className="size-4 text-muted-foreground" aria-hidden />}
      data-testid={sectionTestId}
      headerAction={
        <Badge variant="warning" size="sm">
          Preview
        </Badge>
      }
    >
      <div className="overflow-hidden rounded-xl border border-border/70 bg-background/35">
        <div className="flex flex-col gap-3 border-b border-border/60 px-3 py-3 sm:flex-row sm:items-start sm:justify-between sm:px-4">
          <div className="flex min-w-0 items-start gap-3">
            <span
              className={`flex size-9 shrink-0 items-center justify-center rounded-lg border ${providerAccentClassName}`}
            >
              {providerIcon}
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-medium text-foreground">{subscriptionTitle}</h3>
              <p className="mt-1 max-w-xl text-[13px] leading-[1.45] text-muted-foreground/80">
                {executionDescription}
              </p>
            </div>
          </div>
          <ConnectionStatus
            connection={connection}
            presentation={presentation}
            testId={statusTestId}
          />
        </div>

        <div className="divide-y divide-border/60">
          <SettingsRow
            title="Execution target"
            description={executionTargetDescription(environmentKind)}
            control={
              <div
                className="flex min-w-0 items-center gap-2 rounded-lg border border-border/70 bg-background/40 px-3 py-2 sm:min-w-64"
                aria-label={`Execution target: ${cloudExecutionTargetLabel(environmentKind)}`}
                data-testid={`${sectionTestId}-execution-target`}
              >
                {environmentKind === "cloud" ? (
                  <CloudIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                ) : (
                  <LaptopIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                )}
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {cloudExecutionTargetLabel(environmentKind)}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {environmentLabel}
                  </span>
                </span>
              </div>
            }
          />

          <SettingsRow
            title={`${providerName} subscription`}
            description={subscriptionDescriptionWithAvailability}
            control={
              <div className="flex flex-wrap items-center justify-end gap-2">
                {onRetry ? (
                  <Button size="xs" variant="ghost-muted" disabled={actionBusy} onClick={onRetry}>
                    <RefreshCwIcon className="size-3" aria-hidden />
                    Refresh status
                  </Button>
                ) : null}
                {connection.status === "connected" ? (
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={readOnly || actionBusy || !authAvailable}
                    title={!authAvailable ? authUnavailableReason : undefined}
                    onClick={() => void disconnect()}
                  >
                    {actionBusy ? (
                      <LoaderIcon className="size-3 animate-spin" aria-hidden />
                    ) : (
                      <UnplugIcon className="size-3" aria-hidden />
                    )}
                    Copy logout command
                  </Button>
                ) : (
                  <Button
                    size="xs"
                    disabled={
                      readOnly || actionBusy || connection.status === "loading" || !authAvailable
                    }
                    title={!authAvailable ? authUnavailableReason : undefined}
                    onClick={() => setConnectDialogOpen(true)}
                  >
                    {actionBusy ? (
                      <LoaderIcon className="size-3 animate-spin" aria-hidden />
                    ) : (
                      <TerminalIcon className="size-3" aria-hidden />
                    )}
                    Copy login command
                  </Button>
                )}
              </div>
            }
          />
        </div>

        {environmentKind === "cloud" && !cloudReady ? (
          <div className="border-t border-border/60 px-3 py-3 sm:px-4">
            <Alert variant="warning" controlAlignment="first-line">
              <AlertCircleIcon aria-hidden />
              <AlertTitle>{cloudWarningTitle}</AlertTitle>
              <AlertDescription>{cloudWarningDescription}</AlertDescription>
            </Alert>
          </div>
        ) : null}

        <div className="flex items-start gap-2 border-t border-border/60 bg-muted/18 px-3 py-3 text-xs text-muted-foreground sm:px-4">
          <SectionIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <p>
            <span className="font-medium text-foreground/85">Billing stays separate.</span>{" "}
            {billingDescription}
          </p>
        </div>

        {actionError ? (
          <div className="border-t border-border/60 px-3 py-3 sm:px-4">
            <Alert variant="error" controlAlignment="first-line">
              <AlertCircleIcon aria-hidden />
              <AlertTitle>{providerName} connection not changed</AlertTitle>
              <AlertDescription>{actionError}</AlertDescription>
            </Alert>
          </div>
        ) : null}
        {actionPending ? (
          <div className="border-t border-border/60 px-3 py-3 sm:px-4">
            <Alert variant="info" controlAlignment="first-line">
              <TerminalIcon aria-hidden />
              <AlertTitle>Finish in the environment terminal</AlertTitle>
              <AlertDescription>{actionPending}</AlertDescription>
            </Alert>
          </div>
        ) : null}
      </div>

      <Dialog open={connectDialogOpen} onOpenChange={closeConnectDialog}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>Connect {providerName} subscription</DialogTitle>
            <DialogDescription>
              Fabrica requests the allowlisted command for {environmentLabel} and copies it.
              Authentication stays inside the official {providerName} CLI in that environment and is
              reused by every repository and task there.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(event) => void submitConnection(event)}>
            <DialogPanel className="space-y-4">
              <ol className="list-decimal space-y-2 ps-5 text-sm text-muted-foreground">
                <li>Fabrica copies the official login command to your clipboard.</li>
                <li>
                  Open any thread in {environmentLabel}, open its terminal, and paste{" "}
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
                    {loginCommand}
                  </code>
                  .
                </li>
                <li>
                  Complete the official {providerName} OAuth flow there, then use Refresh status
                  here.
                </li>
              </ol>
              {actionState.status === "error" ? (
                <Alert variant="error" controlAlignment="first-line">
                  <AlertCircleIcon aria-hidden />
                  <AlertDescription>{actionState.message}</AlertDescription>
                </Alert>
              ) : null}
            </DialogPanel>
            <DialogFooter>
              <DialogClose render={<Button type="button" variant="ghost" disabled={actionBusy} />}>
                Cancel
              </DialogClose>
              <Button type="submit" disabled={actionBusy}>
                {actionBusy ? (
                  <LoaderIcon className="size-3.5 animate-spin" aria-hidden />
                ) : (
                  <TerminalIcon className="size-3.5" aria-hidden />
                )}
                {actionBusy ? "Copying command…" : "Copy login command"}
              </Button>
            </DialogFooter>
          </form>
        </DialogPopup>
      </Dialog>
    </SettingsSection>
  );
}

export function CloudProviderSettingsPage(props: CloudProviderSettingsProps) {
  return (
    <SettingsPageContainer>
      <CloudProviderSettings {...props} />
    </SettingsPageContainer>
  );
}
